import Fuse from 'fuse.js';
import { useCallback, useMemo } from 'react';

import { Blob as BlobType } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';

import { SortConfig } from '../components/SortButton';
import { useDocumentTree } from '../context/DocumentTreeContext';
import { countDescendantBlobs, getParentNode } from '../utils/treeUtils';

// Define the FlatTreeNode interface
export interface FlatTreeNode {
  id: string;
  type: 'folder' | 'file';
  level: number;
  data: CollectionNode | BlobType;
  isExpanded?: boolean;
  isSelected?: boolean;
  isRoot?: boolean;
  hasContent?: boolean;
  totalFilesCount?: number;
  isAllSelected?: boolean;
  matchesSearch?: boolean;
}

export function useVirtualizedTree(
  rootNode: CollectionNode,
  blobs: BlobType[] | undefined,
  searchQuery: string,
  expandedFolders: Set<string>,
  activeNodeId: string,
  isFolderFullySelected: (
    folderId: string,
    blobs: BlobType[] | undefined,
    node?: CollectionNode,
  ) => boolean,
  fuse: Fuse<BlobType> | null,
  sortConfig: SortConfig = { field: 'default', direction: 'asc' },
) {
  // Get nodeMaps from the context instead of computing it locally
  const { nodeMaps } = useDocumentTree();

  // Helper function to sort blobs
  const sortBlobs = useCallback(
    (blobsToSort: BlobType[]): BlobType[] => {
      // Return original order for 'default' field
      if (sortConfig.field === 'default') {
        return blobsToSort;
      }
      return [...blobsToSort].sort((a, b) => {
        let comparison = 0;
        if (sortConfig.field === 'name') {
          comparison = a.name.localeCompare(b.name);
        } else if (sortConfig.field === 'date') {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          comparison = dateA - dateB;
        }
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    },
    [sortConfig.field, sortConfig.direction],
  );

  // Helper function to sort collection nodes
  const sortCollections = useCallback(
    (nodes: CollectionNode[]): CollectionNode[] => {
      // Return original order for 'default' field
      if (sortConfig.field === 'default') {
        return nodes;
      }
      return [...nodes].sort((a, b) => {
        let comparison = 0;
        if (sortConfig.field === 'name') {
          comparison = a.name.localeCompare(b.name);
        } else if (sortConfig.field === 'date') {
          const dateA = new Date(a.created_at || 0).getTime();
          const dateB = new Date(b.created_at || 0).getTime();
          comparison = dateA - dateB;
        }
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    },
    [sortConfig.field, sortConfig.direction],
  );

  // Filter files based on search query using Fuse
  const matchingFiles = useMemo(() => {
    if (!searchQuery || !blobs || !fuse) return [];

    // Use Fuse.js search instead of simple filtering
    return fuse.search(searchQuery).map((result) => result.item);
  }, [searchQuery, blobs, fuse]);

  // Create a set of matching file IDs for faster lookups
  const matchingFileIds = useMemo(() => {
    return new Set(matchingFiles.map((file) => file.id));
  }, [matchingFiles]);

  // Collect all parent folder IDs that contain matching files
  const relevantFolderIds = useMemo(() => {
    if (!searchQuery || !matchingFiles.length) return new Set<string>();

    const folderIds = new Set<string>();
    const { nodeToPathMap } = nodeMaps;

    // Add all parent folders using paths
    matchingFiles.forEach((file) => {
      const collectionId = file.collection_details.id;
      folderIds.add(collectionId);

      // Get the path and add all parent folders
      const path = nodeToPathMap.get(collectionId);
      if (path) {
        // Add all path components as folder IDs
        const parts = path.split('/');
        parts.forEach((part: string) => folderIds.add(part));
      }
    });

    return folderIds;
  }, [searchQuery, matchingFiles, nodeMaps]);

  // Flatten the tree structure for rendering
  const flattenedTree = useMemo(() => {
    if (!rootNode || !blobs) return [];

    const result: FlatTreeNode[] = [];

    // Helper function to recursively flatten the tree
    const flattenNode = (
      node: CollectionNode,
      level: number,
      isRoot = false,
      rootNodeId?: string,
    ) => {
      // During search, only include folders that contain matching files
      if (searchQuery && !isRoot && !relevantFolderIds.has(node.id)) {
        return;
      }

      // Calculate node properties
      const nodeFiles = blobs.filter(
        (blob) => blob.collection_details.id === node.id,
      );

      const matchingNodeFiles = searchQuery
        ? nodeFiles.filter((file) => matchingFileIds.has(file.id))
        : nodeFiles;

      const hasFiles = nodeFiles.length > 0;
      const hasMatchingFiles = matchingNodeFiles.length > 0;

      // Force expansion during search for relevant folders
      const isExpanded = searchQuery
        ? relevantFolderIds.has(node.id)
        : expandedFolders.has(node.id);

      const isSelected = node.id === activeNodeId;

      // Always use the optimized counting function
      const totalFilesCount = countDescendantBlobs(node.id, blobs, nodeMaps);
      const hasContent = totalFilesCount > 0;
      const isAllSelected = isFolderFullySelected(node.id, blobs, node);

      // Add this folder to the flattened result
      result.push({
        id: node.id,
        type: 'folder',
        level,
        data: node,
        isExpanded,
        isSelected,
        isRoot,
        hasContent,
        totalFilesCount,
        isAllSelected,
        matchesSearch: searchQuery ? relevantFolderIds.has(node.id) : false,
      });

      // Add children if expanded or searching
      if (isExpanded) {
        // Add files directly in this folder (sorted)
        if (searchQuery) {
          // Only add matching files when searching (sorted)
          sortBlobs(matchingNodeFiles).forEach((file) => {
            result.push({
              id: file.id,
              type: 'file',
              level: level + 1,
              data: file,
              matchesSearch: true,
              isSelected: file.id === activeNodeId,
            });
          });
        } else {
          // Add all files when not searching (sorted)
          sortBlobs(nodeFiles).forEach((file) => {
            result.push({
              id: file.id,
              type: 'file',
              level: level + 1,
              data: file,
              isSelected: file.id === activeNodeId,
            });
          });
        }

        // Recursively add subfolders (sorted)
        if (node.children && node.children.length > 0) {
          sortCollections(node.children).forEach((childNode) => {
            flattenNode(childNode, level + 1, false, rootNodeId || node.id);
          });
        }
      }
    };

    // Start flattening from the root node
    flattenNode(rootNode, 0, true, rootNode.id);
    return result;
  }, [
    rootNode,
    blobs,
    expandedFolders,
    activeNodeId,
    searchQuery,
    matchingFileIds,
    relevantFolderIds,
    isFolderFullySelected,
    nodeMaps,
    sortBlobs,
    sortCollections,
  ]);

  return {
    flattenedTree,
    matchingFiles,
    matchingFileIds,
    relevantFolderIds,
    findParentCollection: (collectionId: string) =>
      getParentNode(collectionId, nodeMaps),
  };
}
